// Sends the user back to the login screen when the API stops accepting their
// token, and to a connection error page when the API cannot be reached at all.
// Most store actions call fetch() directly, so rather than teach each of them
// about either, fetch itself is wrapped once at startup.
//
// Only requests to our own API count. A 401 from the login call itself is left
// alone: it just means a wrong password, which the login form already reports.
// Callers still get the same response or error they always did, so they fail
// the way they always have while the redirect happens.
import store from "../store/index.js";
import router from "../router";

let redirecting = false;
let connectionLost = false;

function requestUrl(input) {
  if (typeof input === "string") return input;
  if (input && input.url) return input.url;
  return String(input || "");
}

function isApiRequest(url) {
  const apiUrl = store.getters["config/getApiUrl"];
  return !!apiUrl && url.startsWith(apiUrl);
}

function isLoginRequest(url) {
  return url.toLowerCase().includes("/authenticate/");
}

export async function endSession() {
  // A page fires several requests at once; only the first 401 acts.
  if (redirecting) return;
  redirecting = true;

  await store.dispatch("auth/logout");

  const current = router.currentRoute.value;
  if (current.name === "login-form") return;
  router.replace({
    name: "login-form",
    query: { redirect: current.fullPath },
  });
}

// fetch rejects, rather than answering, when the server refuses the connection
// (ERR_CONNECTION_REFUSED), DNS fails or the network is down. An abort is the
// caller cancelling on purpose, not a lost connection.
function showConnectionError() {
  if (connectionLost) return;
  connectionLost = true;

  const current = router.currentRoute.value;
  if (current.name === "connection-error") return;
  router.replace({
    name: "connection-error",
    query: { redirect: current.fullPath },
  });
}

export function installSessionGuard() {
  const nativeFetch = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    const url = requestUrl(input);
    let response;
    try {
      response = await nativeFetch(input, init);
    } catch (error) {
      if (error.name !== "AbortError" && isApiRequest(url)) showConnectionError();
      throw error;
    }
    if (response.status === 401 && isApiRequest(url) && !isLoginRequest(url)) {
      endSession();
    }
    return response;
  };

  // Ready to act again once the user has moved past the page we sent them to.
  router.afterEach((to) => {
    if (to.name !== "login-form") redirecting = false;
    if (to.name !== "connection-error") connectionLost = false;
  });
}
